import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import '../SignUpPage/auth-pages.css';

import captCrest from '../Assets/CAPT_crest.jpg';
import { LuTimerReset } from "react-icons/lu";

export default function OTPPage() {
    const navigate = useNavigate();
    const location = useLocation();

    const [OTP, setOTP] = useState('');
    // Fix: useRef instead of state — persists across renders, no re-renders => singular OTP sent
    const otpSentRef = useRef(false);
    const { username, purpose } = location.state || {};
    const password = purpose === 'signup' ? sessionStorage.getItem('signup_password') : null;

    // Lock-out feature — all original constants & logic preserved
    const MAX_ATTEMPTS    = 3;
    const LOCK_SECONDS    = 60;
    const RESEND_COOLDOWN = 30;
    const [attemptsLeft,     setAttemptsLeft]     = useState(MAX_ATTEMPTS);
    const [lockSecondsLeft,  setLockSecondsLeft]  = useState(0);
    const [resendSecondsLeft,setResendSecondsLeft]= useState(0);
    const [otpError,         setOtpError]         = useState('');
    const [loading,          setLoading]          = useState(false);

    const tickIntervalRef   = useRef(null);
    const resendIntervalRef = useRef(null);
    const now = () => Date.now();

    const startLockout = (seconds = LOCK_SECONDS) => {
        const unlockAt = now() + seconds * 1000;
        sessionStorage.setItem('otp_unlock_at', String(unlockAt));
        setLockSecondsLeft(seconds);
        if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = setInterval(() => {
            const msLeft = parseInt(sessionStorage.getItem('otp_unlock_at') || '0', 10) - now();
            const secs   = Math.max(0, Math.ceil(msLeft / 1000));
            setLockSecondsLeft(secs);
            if (secs <= 0) {
                clearInterval(tickIntervalRef.current);
                tickIntervalRef.current = null;
                sessionStorage.removeItem('otp_unlock_at');
                setAttemptsLeft(MAX_ATTEMPTS);
            }
        }, 250);
    };

    const startResendCooldown = (seconds = RESEND_COOLDOWN) => {
        const resendAt = now() + seconds * 1000;
        sessionStorage.setItem('otp_resend_at', String(resendAt));
        setResendSecondsLeft(seconds);
        if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = setInterval(() => {
            const msLeft = parseInt(sessionStorage.getItem('otp_resend_at') || '0', 10) - now();
            const secs   = Math.max(0, Math.ceil(msLeft / 1000));
            setResendSecondsLeft(secs);
            if (secs <= 0) {
                clearInterval(resendIntervalRef.current);
                resendIntervalRef.current = null;
                sessionStorage.removeItem('otp_resend_at');
            }
        }, 250);
    };

    useEffect(() => {
        // Restore lockout
        const unlockAt = parseInt(sessionStorage.getItem('otp_unlock_at') || '0', 10);
        if (unlockAt > now()) startLockout(Math.ceil((unlockAt - now()) / 1000));
        // Restore resend cooldown
        const resendAt = parseInt(sessionStorage.getItem('otp_resend_at') || '0', 10);
        if (resendAt > now()) startResendCooldown(Math.ceil((resendAt - now()) / 1000));
        return () => {
            if (tickIntervalRef.current)   clearInterval(tickIntervalRef.current);
            if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
        };
        // Restore persisted timers only when this page is first mounted.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        sendOTP();
        // Send once for the navigation state that opened this page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── original sendOTP logic, unchanged ─────────────────────────────────
    const sendOTP = async () => {
        if (resendSecondsLeft > 0) return;
        if (otpSentRef.current)    return;
        otpSentRef.current = true;

        let otp_endp = '';
        if (purpose === 'signup')         otp_endp = '/auth/send-signup-otp/';
        else if (purpose === 'forgetPassword') otp_endp = '/auth/send-reset-otp/';

        if (!otp_endp) {
            alert('Invalid purpose for OTP');
            navigate('/');
            return;
        }

        const response = await fetch(
            process.env.REACT_APP_BACKEND_URL + otp_endp,
            {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ username }),
            }
        );
        const data = await response.json();
        if (data.message) {
            // OTP sent successfully — no alert needed (banner shows instead)
        } else {
            alert(data.error || 'OTP failed.');
            otpSentRef.current = false;
        }
        startResendCooldown();
    };

    // ── original handleOTP logic, unchanged ───────────────────────────────
    const handleOTP = async () => {
        setOtpError('');
        setLoading(true);
        try {
            if (lockSecondsLeft > 0) {
                setOtpError(`Too many attempts. Try again in ${lockSecondsLeft}s.`);
                return;
            }
            if (/\D/.test(OTP) || OTP.length !== 6) {
                throw new Error('Invalid OTP');
            }

            let endpoint = '';
            if (purpose === 'signup')              endpoint = '/auth/verify-signup-otp/';
            else if (purpose === 'forgetPassword') endpoint = '/auth/verify-reset-otp/';

            let payload = { username, otp: OTP };
            if (purpose === 'signup') payload.password = password;

            const verifyResponse = await fetch(
                process.env.REACT_APP_BACKEND_URL + endpoint,
                {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify(payload),
                }
            );
            const verifyData = await verifyResponse.json();

            if (verifyData.message && purpose === 'signup') {
                sessionStorage.removeItem('signup_password');
                alert('Signup successful! Please log in.');
                navigate('/');
            } else if (verifyData.message) {
                navigate(`/reset-password/${encodeURIComponent(username)}`);
            } else {
                throw new Error(verifyData.error || 'Verification failed');
            }
        } catch (error) {
            const next = attemptsLeft - 1;
            setAttemptsLeft(next);
            if (next > 0) {
                setOtpError(`Incorrect code. ${next} attempt${next !== 1 ? 's' : ''} remaining.`);
            } else if (next <= 0) {
                setOtpError(`Too many attempts. Locked for ${LOCK_SECONDS}s.`);
                startLockout();
            } else {
                setOtpError('Verification failed: ' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = () => {
        if (resendSecondsLeft > 0) return;
        otpSentRef.current = false;
        sendOTP();
        startResendCooldown(RESEND_COOLDOWN);
    };
    // ──────────────────────────────────────────────────────────────────────

    const isLocked = lockSecondsLeft > 0;

    return (
        <div className="ap-page">
            <div className="ap-wrap">

                {/* Brand */}
                <div className="ap-brand">
                    <div className="ap-brand-logo">
                        <div className="ap-logo-icon">
                            <img src={captCrest} alt="CAPT Crest" />
                        </div>
                        <span className="ap-wordmark">NEXUS</span>
                    </div>
                    <span className="ap-brand-sub">Captain Portal · By Tech Comm</span>
                </div>

                {/* Card */}
                <div className="ap-card">
                    <div className="ap-card-accent" />
                    <div className="ap-card-header">
                        <h1 className="ap-card-title">Verification</h1>
                        <p className="ap-card-subtitle">
                            Code sent to <strong style={{ color: 'var(--ap-maroon)' }}>@{username}</strong> via Telegram
                        </p>
                    </div>

                    <div className="ap-card-body">

                        {/* Locked out banner */}
                        {isLocked && (
                            <div className="ap-banner ap-banner--error" style={{ marginBottom: 16 }}>
                                <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                                    <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                                    <path d="M5.5 7V5C5.5 3.6 6.6 2.5 8 2.5S10.5 3.6 10.5 5v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                </svg>
                                Too many attempts. Try again in <strong>&nbsp;{lockSecondsLeft}s</strong>.
                            </div>
                        )}

                        {/* OTP input */}
                        <div className="ap-field">
                            <label className="ap-label" htmlFor="otp-input">6-digit code</label>
                            <div className="ap-input-wrap">
                                <LuTimerReset className="ap-input-icon" style={{ width: 15, height: 15, objectFit: 'contain' }}/>
                                <input
                                    id="otp-input"
                                    className={`ap-input${otpError ? ' ap-input--error' : ''}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="Enter 6-digit code"
                                    value={OTP}
                                    disabled={isLocked}
                                    onChange={e => { setOTP(e.target.value); setOtpError(''); }}
                                    onKeyDown={e => e.key === 'Enter' && !isLocked && handleOTP()}
                                    style={{ letterSpacing: OTP ? '0.3em' : 'normal', fontFamily: OTP ? 'var(--ap-font-head)' : 'var(--ap-font)' }}
                                />
                            </div>
                            {otpError && <span className="ap-error">{otpError}</span>}

                            {/* Attempts remaining indicator */}
                            {!isLocked && attemptsLeft < MAX_ATTEMPTS && attemptsLeft > 0 && (
                                <span className="ap-hint" style={{ color: 'var(--ap-red)' }}>
                                    {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} remaining before lockout
                                </span>
                            )}
                        </div>

                        {/* Verify button */}
                        <button
                            className="ap-btn-primary"
                            onClick={handleOTP}
                            disabled={loading || isLocked || OTP.length !== 6}
                            style={{ marginTop: 4 }}
                        >
                            {loading ? <span className="ap-spinner" /> : 'Verify'}
                        </button>

                        {/* Resend row */}
                        <div className="ap-resend-row">
                            Didn't receive it?
                            <button
                                className="ap-resend-btn"
                                onClick={handleResendOTP}
                                disabled={resendSecondsLeft > 0}
                            >
                                Resend OTP
                            </button>
                            {resendSecondsLeft > 0 && (
                                <span className="ap-countdown">in {resendSecondsLeft}s</span>
                            )}
                        </div>
                    </div>
                </div>

                <p className="ap-below-card">
                    <button className="ap-back-link" onClick={() => navigate(-1)}>
                        <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
                            <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Go back
                    </button>
                </p>
            </div>
        </div>
    );
}
